'use client'

import { useEffect, useRef, useState } from 'react'
import { useAction, useMutation, useQuery } from 'convex/react'
import { useTranslations } from 'next-intl'
import { Loader2, Check, X as XIcon, Globe, MapPin } from 'lucide-react'
import { api } from '../../../../convex/_generated/api'
import { Id } from '../../../../convex/_generated/dataModel'
import { Modal, Button, Input, FormField, Select, Switch, Avatar, Badge, useToast } from '@/design-system'
import LanguageSelector, { languages } from '@/components/LanguageSelector'
import LocationSelector from '@/components/LocationSelector'

interface CreateOrganizationModalProps {
  isOpen: boolean
  onClose: () => void
}

type ScrapeResult = {
  name: string
  faviconStorageId?: Id<'_storage'>
  faviconUrl: string | null
  detectedLanguage: string
  detectedLocationCode: string | null
  detectedLocationId: Id<'locations'> | null
  domain: string | null
  duplicates: Array<{ _id: Id<'organizations'>; name: string; isActive: boolean; createdAt: number }>
}

// Loose "looks like it could be a domain" check — gates whether we even try a
// scrape. Real reachability is determined by scrapeSite itself (best-effort,
// never throws — see convex/admin/orgCreation/scrape.ts).
function looksLikeDomain(raw: string): boolean {
  const trimmed = raw.trim()
  if (!trimmed) return false
  return /^(https?:\/\/)?([\w-]+\.)+[\w-]{2,}(\/.*)?$/i.test(trimmed)
}

export default function CreateOrganizationModal({ isOpen, onClose }: CreateOrganizationModalProps) {
  const t = useTranslations('account.adminOrgCreation.createModal')
  const tCommon = useTranslations('common')
  const toast = useToast()

  const [website, setWebsite] = useState('')
  const [scraped, setScraped] = useState<ScrapeResult | null>(null)
  const [name, setName] = useState('')
  const [language, setLanguage] = useState('en')
  const [locationId, setLocationId] = useState<Id<'locations'> | undefined>(undefined)
  const [planId, setPlanId] = useState<Id<'plans'> | ''>('')
  const [referralCodeId, setReferralCodeId] = useState<Id<'referralCodes'> | ''>('')
  const [ownerEmail, setOwnerEmail] = useState('')
  const [autoOnboard, setAutoOnboard] = useState(true)
  const [manualMode, setManualMode] = useState(false)
  const [detectExpanded, setDetectExpanded] = useState(false)

  const [isScraping, setIsScraping] = useState(false)
  const [scrapeError, setScrapeError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  // Sequence guard against fast typing + slow network (Frontend Review #5 —
  // mirrors InputScreen.tsx's verifyUrl debounce/race protection).
  const seqRef = useRef(0)

  const scrapeSite = useAction(api.admin.orgCreation.scrape.scrapeSite)
  const registerCreatedOrg = useMutation(api.admin.orgCreation.mutations.registerCreatedOrg)
  const plans = useQuery(api.features.plans.queries.listPlans)
  const referralCodes = useQuery(api.admin.referralCodes.listActiveReferralCodesForAdmin)
  const countryLocations = useQuery(api.locations.getLocations, { type: 'country' })

  useEffect(() => {
    if (manualMode) return
    const raw = website.trim()
    setScraped(null)
    setScrapeError(null)
    if (!looksLikeDomain(raw)) {
      setIsScraping(false)
      return
    }
    setIsScraping(true)
    const seq = ++seqRef.current
    const timeout = setTimeout(async () => {
      try {
        const result = await scrapeSite({ url: raw })
        if (seq !== seqRef.current) return
        setScraped(result)
        setName(result.name)
        setLanguage(result.detectedLanguage)
        setLocationId(result.detectedLocationId ?? undefined)
        setIsScraping(false)
      } catch (err) {
        if (seq !== seqRef.current) return
        console.error('[CreateOrganizationModal/scrapeSite]', err)
        setScrapeError(t('scrapeErrorDefault'))
        setIsScraping(false)
      }
    }, 400)
    return () => clearTimeout(timeout)
  }, [website, manualMode, scrapeSite, t])

  const resetState = () => {
    setWebsite('')
    setScraped(null)
    setName('')
    setLanguage('en')
    setLocationId(undefined)
    setPlanId('')
    setReferralCodeId('')
    setOwnerEmail('')
    setAutoOnboard(true)
    setManualMode(false)
    setDetectExpanded(false)
    setIsScraping(false)
    setScrapeError(null)
    setSubmitError(null)
  }

  const handleClose = () => {
    if (isSubmitting) return
    resetState()
    onClose()
  }

  const trimmedWebsite = website.trim()
  const trimmedName = name.trim()
  const isReady = manualMode ? trimmedName.length >= 2 : !!scraped
  const canSubmit = isReady && !isSubmitting && !isScraping

  const handleSubmit = async () => {
    if (!canSubmit) return
    setIsSubmitting(true)
    setSubmitError(null)
    try {
      const clerkRes = await fetch('/api/admin/organizations/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: trimmedName || trimmedWebsite }),
      })
      if (!clerkRes.ok) {
        const message = await clerkRes.text()
        throw new Error(message || t('createFailed'))
      }
      const clerkOrg = (await clerkRes.json()) as { id: string; name: string }

      await registerCreatedOrg({
        clerkOrgId: clerkOrg.id,
        name: trimmedName || clerkOrg.name,
        domain: manualMode ? trimmedWebsite || undefined : scraped?.domain ?? undefined,
        faviconStorageId: manualMode ? undefined : scraped?.faviconStorageId,
        planId: planId || undefined,
        referralCodeId: referralCodeId || undefined,
        language,
        locationId,
        ownerEmail: ownerEmail.trim() || undefined,
        autoOnboard,
      })

      toast.success(
        t('successTitle'),
        autoOnboard
          ? t('successOnboardingDetail', { name: trimmedName || clerkOrg.name })
          : t('successManualDetail', { name: trimmedName || clerkOrg.name }),
      )
      resetState()
      onClose()
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err)
      setSubmitError(message)
    } finally {
      setIsSubmitting(false)
    }
  }

  const planOptions = [
    { label: t('planDefaultOption'), value: '' },
    ...(plans ?? []).map((p) => ({ label: p.name, value: p._id as string })),
  ]
  const referralOptions = [
    { label: t('referralNoneOption'), value: '' },
    ...(referralCodes ?? []).map((code) => ({
      label: `${code.code} — ${code.partnerName}${code.lsDiscountCode ? ` · ${t('discountSuffix')}` : ''}${
        code.trialDays > 0 ? ` · ${code.trialDays}${t('trialDaysSuffix')}` : ''
      }`,
      value: code._id as string,
    })),
  ]

  const languageName = languages.find((l) => l.id === language)?.name ?? language
  const locationName = countryLocations?.find((loc) => loc._id === locationId)?.name ?? t('locationUnknown')

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('title')}
      icon={<Globe className="w-6 h-6 text-[var(--color-lime)]" />}
      maxWidth="md"
      footer={
        <div className="flex items-center justify-end gap-3">
          {submitError && (
            <span className="mr-auto text-xs" style={{ color: 'var(--color-pink)' }}>
              {submitError}
            </span>
          )}
          <Button variant="ghost" size="sm" onClick={handleClose} disabled={isSubmitting}>
            {tCommon('cancel')}
          </Button>
          <Button size="sm" onClick={handleSubmit} disabled={!canSubmit} loading={isSubmitting}>
            {autoOnboard ? t('submitWithOnboarding') : t('submitWithoutOnboarding')}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <div>
          <FormField label={t('websiteLabel')} htmlFor="admin-org-website">
            <Input
              id="admin-org-website"
              value={website}
              onChange={(e) => {
                setWebsite(e.target.value)
                setManualMode(false)
              }}
              placeholder={t('websitePlaceholder')}
              autoComplete="off"
              autoFocus
              disabled={isSubmitting}
              error={!!scrapeError}
              rightAddon={
                isScraping ? (
                  <Loader2 className="w-4 h-4 animate-spin" style={{ color: 'var(--color-text-muted)' }} />
                ) : scraped ? (
                  <Check className="w-4 h-4" style={{ color: 'var(--color-lime)' }} />
                ) : scrapeError ? (
                  <XIcon className="w-4 h-4" style={{ color: 'var(--color-pink)' }} />
                ) : undefined
              }
            />
          </FormField>

          {!manualMode && (
            <p className="mt-1.5 text-xs" style={{ color: 'var(--color-text-muted)' }}>
              {t('websiteHint')}
            </p>
          )}

          {scrapeError && !manualMode && (
            <div
              className="mt-2 flex items-center justify-between gap-2 rounded-lg px-3 py-2 text-xs"
              style={{ background: 'var(--color-pink-muted)', color: 'var(--color-pink)' }}
            >
              <span>{t('scrapeUnreachable')}</span>
              <button
                type="button"
                className="whitespace-nowrap font-semibold underline"
                onClick={() => {
                  setManualMode(true)
                  setName(website.trim())
                }}
              >
                {t('enterManually')}
              </button>
            </div>
          )}

          {scraped && !manualMode && (
            <div
              className="mt-2.5 flex items-center gap-3 rounded-lg px-3.5 py-3"
              style={{ background: 'var(--color-background)', border: '1px solid var(--color-border)' }}
            >
              <Avatar src={scraped.faviconUrl ?? undefined} fallback={name || scraped.name} size="md" />
              <div className="min-w-0 flex-1">
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  aria-label={t('orgNameEditLabel')}
                  className="w-full border-b border-dashed border-transparent bg-transparent text-sm font-semibold outline-none focus:border-[var(--color-border-light)] hover:border-[var(--color-border-light)]"
                  style={{ color: 'var(--color-text-primary)' }}
                />
                <p className="mt-0.5 text-[11px]" style={{ color: 'var(--color-text-muted)' }}>
                  {scraped.domain ? `${scraped.domain} · ` : ''}
                  {t('detectedFromSite')}
                </p>
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  <button
                    type="button"
                    onClick={() => setDetectExpanded((v) => !v)}
                    className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px]"
                    style={{ background: 'var(--color-cyan-muted)', color: 'var(--color-cyan)' }}
                  >
                    <Globe className="w-3 h-3" /> {languageName}
                  </button>
                  <button
                    type="button"
                    onClick={() => setDetectExpanded((v) => !v)}
                    className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px]"
                    style={{ background: 'var(--color-cyan-muted)', color: 'var(--color-cyan)' }}
                  >
                    <MapPin className="w-3 h-3" /> {locationName}
                  </button>
                </div>
              </div>
            </div>
          )}

          {scraped && !manualMode && scraped.duplicates.length > 0 && (
            <div
              className="mt-2.5 rounded-lg px-3.5 py-2.5 text-xs"
              style={{
                background: 'var(--color-cyan-muted)',
                border: '1px solid rgba(0, 212, 255, 0.3)',
                color: 'var(--color-text-secondary)',
              }}
            >
              <p className="mb-1.5 font-semibold" style={{ color: 'var(--color-cyan)' }}>
                {scraped.duplicates.length === 1
                  ? t('duplicateSingular')
                  : t('duplicatePlural', { count: scraped.duplicates.length })}
              </p>
              <div className="flex flex-col gap-1.5">
                {scraped.duplicates.map((dup) => (
                  <div key={dup._id} className="flex items-center gap-2">
                    <Avatar fallback={dup.name} size="xs" />
                    <span style={{ color: 'var(--color-text-primary)' }}>{dup.name}</span>
                    <Badge variant={dup.isActive ? 'success' : 'default'} size="sm" dot>
                      {dup.isActive ? t('active') : t('inactive')}
                    </Badge>
                    <span className="text-[11px]" style={{ color: 'var(--color-text-muted)' }}>
                      {t('since', { date: new Date(dup.createdAt).toLocaleDateString() })}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {(manualMode || detectExpanded) && (
          <div className="grid grid-cols-2 gap-3">
            <FormField label={t('languageLabel')}>
              <LanguageSelector value={language} onChange={setLanguage} disabled={isSubmitting} />
            </FormField>
            <FormField label={t('locationLabel')}>
              <LocationSelector
                value={locationId}
                onChange={(v) => setLocationId(v as Id<'locations'>)}
                type="country"
                size="small"
                disabled={isSubmitting}
              />
            </FormField>
          </div>
        )}

        {manualMode && (
          <FormField label={t('manualNameLabel')} required>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('manualNamePlaceholder')}
              disabled={isSubmitting}
            />
          </FormField>
        )}

        <div className="grid grid-cols-2 gap-3">
          <FormField label={t('planLabel')}>
            <Select
              value={planId}
              onChange={(v) => setPlanId(v as Id<'plans'> | '')}
              options={planOptions}
              portal
              disabled={isSubmitting}
            />
          </FormField>
          <FormField label={t('referralLabel')}>
            <Select
              value={referralCodeId}
              onChange={(v) => setReferralCodeId(v as Id<'referralCodes'> | '')}
              options={referralOptions}
              portal
              disabled={isSubmitting}
            />
          </FormField>
        </div>

        <FormField label={t('ownerEmailLabel')} helperText={t('ownerEmailHint')}>
          <Input
            type="email"
            value={ownerEmail}
            onChange={(e) => setOwnerEmail(e.target.value)}
            placeholder={t('ownerEmailPlaceholder')}
            disabled={isSubmitting}
          />
        </FormField>

        <div
          className="flex items-start gap-3 rounded-lg px-3.5 py-3"
          style={{ background: 'var(--color-background)', border: '1px solid var(--color-border)' }}
        >
          <Switch checked={autoOnboard} onCheckedChange={setAutoOnboard} disabled={isSubmitting} />
          <div className="flex-1">
            <p className="text-sm font-semibold">{t('autoOnboardTitle')}</p>
            <p className="mt-0.5 text-xs" style={{ color: 'var(--color-text-secondary)' }}>
              {autoOnboard ? t('autoOnboardOnSub') : t('autoOnboardOffSub')}
            </p>
          </div>
        </div>
      </div>
    </Modal>
  )
}
